import { faroCoreApiClient } from "@api/faro-core-api-client";
import { isWorkspaceAdmin } from "@components/table/members/remove-xg-seat/remove-xg-seat-utils";
import { useCompanyId } from "@context-providers/company-id-provider";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { companyMembersSelector } from "@store/members/members-selector";
import { useAppSelector } from "@store/store-helper";

/**
 * Adds the new project manager to the groups they do not belong to yet, as a project manager.
 *
 * @param newManager The member who manages the projects of the groups from now on
 * @param groupIds The groups of the projects
 * @returns the groups whose projects the new manager can manage now. A group they could not be
 * added to is left out, and the user is told why.
 */

/**
 * Returns how to add a new project manager to the groups of the projects they take over, since a
 * project manager belongs to the group of the project. An admin in the workspace is left out,
 * since they can manage the projects of every group already.
 */
export function useAddManagerToGroups() {
  const companyId = useCompanyId();
  const {
    handleErrorWithToast
  } = useErrorContext();
  const workspaceMembers = useAppSelector(companyMembersSelector);
  return async (newManager, groupIds) => {
    const workspaceMember = workspaceMembers.find(({
      identity
    }) => identity === newManager.identity);
    if (workspaceMember && isWorkspaceAdmin(workspaceMember)) {
      return groupIds;
    }
    const managerGroupIds = await getGroupIdsOf(newManager);
    if (!managerGroupIds) {
      return [];
    }
    const joinedGroupIds = [];
    for (const groupId of groupIds) {
      if (managerGroupIds.has(groupId) || (await addToGroup(newManager, groupId))) {
        joinedGroupIds.push(groupId);
      }
    }
    return joinedGroupIds;
  };

  /**
   * Returns the groups the new project manager belongs to, or undefined when that could not be
   * looked up, telling the user why
   */
  async function getGroupIdsOf(newManager_0) {
    try {
      const groups = await faroCoreApiClient.groups.getCompanyMemberGroups({
        companyId,
        userIdentity: newManager_0.identity
      });
      return new Set(groups.map(({
        id
      }) => id));
    } catch (error) {
      handleErrorWithToast({
        id: `getProjectManagerGroups-${Date.now().toString()}`,
        title: "Cannot look up the groups of the new project manager",
        error
      });
      return undefined;
    }
  }

  /**
   * Adds the new project manager to a group as a project manager
   *
   * @returns whether that worked, telling the user why not otherwise
   */
  async function addToGroup(newManager_1, groupId_0) {
    try {
      const response = await faroCoreApiClient.groups.addMembersToGroup({
        companyId,
        groupId: groupId_0,
        payload: {
          assignments: [{
            identities: [newManager_1.identity],
            role: "PROJECT_MANAGER"
          }]
        }
      });
      const failure = response.errorData?.details.failedInvitations[0];
      if (failure) {
        throw new Error(failure.message);
      }
      return true;
    } catch (error_0) {
      handleErrorWithToast({
        id: `addProjectManagerToGroup-${groupId_0}-${Date.now().toString()}`,
        title: "Cannot add the new project manager to the group",
        error: error_0
      });
      return false;
    }
  }
}